import { Link } from "react-router-dom";
import { Calendar, MessageCircle, ShieldCheck, AlertCircle, FileText, UserCheck } from "lucide-react";

/**
 * Surfaces the three most-critical tutor commitments — Assignment Continuity (§20),
 * Enquiry Responsiveness (§21) and No Unauthorised Reassignment (§22) — as side-by-side
 * cards above the application form. Sets honest expectations from day one.
 */
export default function TutorCommitments() {
    return (
        <section data-testid="tutor-commitments" className="py-14 bg-[#F8FAFC] border-y border-slate-100">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="text-center mb-10">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-[10px] uppercase tracking-[0.22em] font-bold text-amber-700 mb-3">
                        <ShieldCheck className="w-3 h-3" /> Tutor Commitments — what every parent expects from you
                    </div>
                    <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A]">
                        Three professional commitments before you begin teaching.
                    </h2>
                    <p className="mt-3 text-sm text-slate-500 max-w-2xl mx-auto">
                        These are the three clauses parents care about most — and the three we hold every Anand Tutorials educator to. Read them carefully before you apply.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
                    {/* Clause 1 — Assignment Continuity */}
                    <div data-testid="tutor-commitment-continuity" className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 hover:border-[#1E3A8A]/30 hover:shadow-md transition-all flex flex-col">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-11 h-11 rounded-xl bg-[#1E3A8A] text-white flex items-center justify-center shrink-0">
                                <Calendar className="w-5 h-5" />
                            </div>
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-[#1E3A8A]">Clause § 20</div>
                                <div className="font-heading font-bold text-base text-[#0F172A] leading-tight">Assignment Continuity & Professional Commitment</div>
                            </div>
                        </div>
                        <p className="text-sm text-slate-600 leading-relaxed mb-4">
                            Once you accept an assignment, you carry the student's academic continuity. We expect consistency, reliability and respect for the student's learning rhythm.
                        </p>
                        <ul className="space-y-2.5 text-[13px] text-slate-700 flex-1">
                            <CommitmentBullet>Provide a <strong>genuine valid reason</strong> if you wish to discontinue any assignment.</CommitmentBullet>
                            <CommitmentBullet>Give <strong>30 days (1 month) prior notice</strong> in writing before exiting.</CommitmentBullet>
                            <CommitmentBullet><strong>Complete the ongoing monthly cycle</strong> — never abandon a student mid-month.</CommitmentBullet>
                            <CommitmentBullet>Only an unavoidable emergency exempts the notice requirement — inform the team immediately so we can transition smoothly.</CommitmentBullet>
                        </ul>
                        <div className="mt-5 p-3 rounded-lg bg-rose-50 border border-rose-100 text-xs text-rose-800 flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                            <span>Repeated abrupt discontinuations are reviewed and may result in suspension or removal from the Platform's roster.</span>
                        </div>
                    </div>

                    {/* Clause 2 — Enquiry Responsiveness */}
                    <div data-testid="tutor-commitment-responsiveness" className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 hover:border-[#1E3A8A]/30 hover:shadow-md transition-all flex flex-col">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-11 h-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0">
                                <MessageCircle className="w-5 h-5" />
                            </div>
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Clause § 21</div>
                                <div className="font-heading font-bold text-base text-[#0F172A] leading-tight">Enquiry Responsiveness & Parent Communication</div>
                            </div>
                        </div>
                        <p className="text-sm text-slate-600 leading-relaxed mb-4">
                            Every lead we send you is a parent's trust in motion. Treat it as a priority — speed and clarity in communication are non-negotiable.
                        </p>
                        <ul className="space-y-2.5 text-[13px] text-slate-700 flex-1">
                            <CommitmentBullet>Coordinate with assigned parent / student <strong>the same day</strong> — within 24 hours at the latest.</CommitmentBullet>
                            <CommitmentBullet>Maintain timely follow-ups through the demo, onboarding and first-class window.</CommitmentBullet>
                            <CommitmentBullet>Reply to parent and coordinator messages <strong>within 24 hours</strong> for the entire active assignment.</CommitmentBullet>
                            <CommitmentBullet>If you are temporarily unavailable, <strong>tell us immediately</strong> — we'll reassign rather than keep parents waiting.</CommitmentBullet>
                        </ul>
                        <div className="mt-5 p-3 rounded-lg bg-emerald-50 border border-emerald-100 text-xs text-emerald-900 flex items-start gap-2">
                            <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
                            <span>Tutors who consistently respond fast get priority for premium NRI and senior-grade leads.</span>
                        </div>
                    </div>

                    {/* Clause 3 — No Unauthorised Reassignment */}
                    <div data-testid="tutor-commitment-no-substitution" className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 hover:border-[#1E3A8A]/30 hover:shadow-md transition-all flex flex-col">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-11 h-11 rounded-xl bg-[#7C3AED] text-white flex items-center justify-center shrink-0">
                                <UserCheck className="w-5 h-5" />
                            </div>
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-[#7C3AED]">Clause § 22</div>
                                <div className="font-heading font-bold text-base text-[#0F172A] leading-tight">No Unauthorised Reassignment, Substitution or Referral</div>
                            </div>
                        </div>
                        <p className="text-sm text-slate-600 leading-relaxed mb-4">
                            You were chosen for this assignment based on your verified credentials. Personally handling the class — start to finish — is a non-negotiable promise to the parent.
                        </p>
                        <ul className="space-y-2.5 text-[13px] text-slate-700 flex-1">
                            <CommitmentBullet><strong>Personally conduct</strong> every assigned class — no transfer, outsource or proxy.</CommitmentBullet>
                            <CommitmentBullet>Do not refer the student / parent to any other tutor, friend, colleague, coaching institute or third party without prior written approval.</CommitmentBullet>
                            <CommitmentBullet>Even temporary replacements (illness / travel) must be routed through Anand Tutorials so a verified substitute is allocated.</CommitmentBullet>
                            <CommitmentBullet>Know a teacher who'd like to work with us? Have them <strong>register directly</strong> through the official Become-a-Tutor process.</CommitmentBullet>
                        </ul>
                        <div className="mt-5 p-3 rounded-lg bg-violet-50 border border-violet-100 text-xs text-violet-900 flex items-start gap-2">
                            <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
                            <span>Protects quality, verification, parent trust, academic consistency and platform accountability.</span>
                        </div>
                    </div>
                </div>

                <div className="mt-8 flex flex-wrap items-center justify-center gap-2 text-xs text-slate-500">
                    <FileText className="w-3.5 h-3.5" />
                    Full clauses and the complete Tutor Code of Engagement are available on the
                    <Link to="/policies/tutor" data-testid="tutor-commitments-policy-link" className="text-[#1E3A8A] font-semibold hover:underline">
                        Tutor Policy page
                    </Link>
                    · By submitting the application below you confirm you have read and accept these terms.
                </div>
            </div>
        </section>
    );
}

function CommitmentBullet({ children }) {
    return (
        <li className="flex items-start gap-2.5">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
            <span className="leading-relaxed">{children}</span>
        </li>
    );
}
